<template>
  <LocationMatrixTable
    :icon="icon"
    :locations="locations"
    :rows="rows"
    :title="title"
    :total="total"
    :unit-hint="GROUP_HOURS_UNIT_HINT[group]" />
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { ClockCogIcon, ClockIcon } from 'vue-tabler-icons';
import { useMetricsStore } from '@/stores/metrics';
import type { HourGroup, HoursAndLessons } from '@/models/staffMetrics';
import type { LocationMatrixCell, LocationMatrixRow } from '@/models/metricDefinitions';
import { formatGroupHours, GROUP_HOURS_UNIT_HINT } from '@/utils/formatters';
import LocationMatrixTable from '@/components/metrics/LocationMatrixTable.vue';

interface Props {
  group: HourGroup;
}

const props = defineProps<Props>();

const metricsStore = useMetricsStore();

const icon = computed(() => (props.group === 'pedagogical' ? ClockIcon : ClockCogIcon));
const title = computed(() => (props.group === 'pedagogical' ? 'PMA-Stunden' : 'VMA-Stunden'));

const locations = computed(() => Object.keys(metricsStore.metrics).sort((a, b) => a.localeCompare(b, 'de')));

const isLoading = (location: string) => metricsStore.loadingStates[location] ?? false;
const isAnyLoading = computed(() => locations.value.some(isLoading));

const itemsByLocation = computed(() =>
  locations.value.map((name) => metricsStore.metrics[name].hoursByCategory.filter((item) => item.group === props.group))
);

const sum = (items: HoursAndLessons[]): HoursAndLessons =>
  items.reduce((acc, item) => ({ hours: acc.hours + item.hours, lessons: acc.lessons + item.lessons }), { hours: 0, lessons: 0 });

// Each location has its own database, so category ids differ between them and only the label identifies a category.
// Locations need not share all categories, so the rows are their union in the order the backends send them.
const categories = computed(() => [...new Set(itemsByLocation.value.flat().map((item) => item.label))]);

// hours[category][location], zero where a location lacks the category
const hours = computed((): HoursAndLessons[][] =>
  categories.value.map((label) => itemsByLocation.value.map((items) => sum(items.filter((item) => item.label === label))))
);

const toCell = ({ hours, lessons }: HoursAndLessons, loading: boolean): LocationMatrixCell => ({
  ...formatGroupHours(props.group, hours, lessons),
  isLoading: loading,
  empty: hours === 0
});

const toRow = (key: string | number, label: string, values: HoursAndLessons[]): LocationMatrixRow => ({
  key,
  label,
  cells: values.map((value, index) => toCell(value, isLoading(locations.value[index]))),
  total: toCell(sum(values), isAnyLoading.value)
});

// Listed below the categories and counted in the total, so the total matches the group's hours on the cards.
const unassigned = computed(() => locations.value.map((name) => metricsStore.metrics[name].unassignedHours[props.group]));

const rows = computed(() => [
  ...categories.value.map((label, index) => toRow(label, label, hours.value[index])),
  toRow('unassigned', 'Nicht zugeordnet', unassigned.value)
]);

const total = computed(() =>
  toRow(
    'total',
    'Gesamt',
    locations.value.map((_, index) => sum([...hours.value, unassigned.value].map((row) => row[index])))
  )
);
</script>
